import { X, ExternalLink, Package } from 'lucide-react';
import { RetailerProduct } from '../../types/retailerProduct';

interface Props {
  ingredientName: string;
  products: RetailerProduct[];
  onClose: () => void;
}

export function ScrapingResultModal({ ingredientName, products, onClose }: Props) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-menioo-navy">
              Produits Carrefour — {ingredientName}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {products.length === 0
                ? 'Aucun produit trouvé'
                : `${products.length} produit${products.length > 1 ? 's' : ''} trouvé${products.length > 1 ? 's' : ''}`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-3">
          {products.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Package className="h-10 w-10 mx-auto mb-2 text-gray-300" />
              <p>Aucun produit trouvé pour cet ingrédient sur Carrefour.</p>
            </div>
          ) : (
            products.map((product) => (
              <div
                key={product.id}
                className="flex items-start gap-3 p-3 border border-gray-100 rounded-lg hover:bg-gray-50"
              >
                {product.imageUrl ? (
                  <img
                    src={product.imageUrl}
                    alt={product.title}
                    className="h-14 w-14 object-contain rounded border border-gray-100 flex-shrink-0"
                  />
                ) : (
                  <div className="h-14 w-14 bg-gray-100 rounded flex items-center justify-center flex-shrink-0">
                    <Package className="h-6 w-6 text-gray-300" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{product.title}</p>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    {product.brand && (
                      <span className="text-xs text-gray-500">{product.brand}</span>
                    )}
                    {product.format && (
                      <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                        {product.format}
                      </span>
                    )}
                    {product.isOwnBrand && (
                      <span className="text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">
                        MDD
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-1 font-mono">{product.retailerProductId}</p>
                </div>
                <a
                  href={product.productUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-menioo-green hover:text-menioo-green-dark flex-shrink-0"
                  title="Voir sur Carrefour"
                >
                  <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}
